<template>
  <div class="card p-shadow-app">
    <h1>Extranet Users</h1>
    <Toast :style="{ width: $isMobile() ? '85vw' : '' }" />
    <ConfirmDialog></ConfirmDialog>
    <div style="height: 65vh">
      <DataTable
        :value="extranetUsers"
        :filters.sync="filters"
        filterDisplay="row"
        :loading="loading"
        :paginator="true"
        :rows="20"
        class="p-datatable-sm"
        responsiveLayout="scroll"
        removableSort
        sortMode="single"
        :scrollable="true"
        scrollHeight="flex"
        :autoLayout="true"
        stripedRows
      >
        <template #header>
          <div class="table-header p-d-flex p-jc-between p-ai-center">
            <span style="flex-grow: 1" class="p-d-flex p-ai-center">
              <Button
                v-if="can('createExtranetUser')"
                icon="pi pi-plus"
                class="p-button-sm p-button-primary"
                @click="openUserDialog()"
                aria-label="Novo Utilizador"
              />
            </span>
          </div>
        </template>

        <template #empty>Nenhum utilizador encontrado.</template>
        <template #loading>A carregar utilizadores...</template>

        <Column
          v-for="col in columns"
          :key="col.field"
          :field="col.field"
          :header="col.header"
          :filterField="col.field"
          sortable
        >
          <template #filter="{ filterModel, filterCallback }">
            <Dropdown
              v-if="col.field === 'activeString'"
              v-model="filterModel.value"
              @input="filterCallback()"
              :options="[
                { value: 0, label: 'Não' },
                { value: 1, label: 'Sim' }
              ]"
              optionValue="value"
              optionLabel="label"
              class="p-column-filter"
              :showClear="true"
            />

            <InputText
              v-else-if="filterModel"
              v-model="filterModel.value"
              class="p-column-filter"
              @input="filterCallback()"
            />
          </template>
        </Column>

        <Column>
          <template #body="{ data }">
            <Button
              v-if="can('updateExtranetUser')"
              icon="pi pi-pencil"
              v-tooltip.left="'Editar'"
              class="p-button-sm p-button-rounded p-button-success"
              @click="openUserDialog(data)"
            />
          </template>
        </Column>
      </DataTable>

      <Dialog
        :style="{ width: $isMobile() ? '100vw' : '40vw' }"
        :visible.sync="userDialog"
        :header="(userForm.id == undefined ? 'Novo' : 'Editar') + ' Utilizador'"
        :modal="true"
        :draggable="false"
        :closable="true"
      >
        <div class="dialog-content-wrapper">
          <div v-if="dialogLoading" class="dialog-overlay">
            <i class="pi pi-spinner pi-spin dialog-spinner"></i>
          </div>
          <div :class="{ 'dialog-content-disabled': dialogLoading }">
            <div class="p-fluid p-formgrid p-grid">
              <div class="p-field" :class="$isMobile() ? 'p-col-12' : 'p-col-6'">
                <label for="name">Nome</label>
                <InputText
                  id="name"
                  name="name"
                  v-validate="'required'"
                  :class="{ 'p-invalid': errors.has('name') }"
                  v-model="userForm.name"
                />
                <small v-if="errors.has('name')" class="p-error" role="alert">
                  Nome é obrigatório
                </small>
              </div>

              <div class="p-field" :class="$isMobile() ? 'p-col-12' : 'p-col-6'">
                <label for="username">Nome de Utilizador</label>
                <InputText
                  id="username"
                  name="username"
                  v-validate="'required'"
                  :class="{ 'p-invalid': errors.has('username') }"
                  v-model="userForm.username"
                />
                <small v-if="errors.has('username')" class="p-error" role="alert">
                  Nome de Utilizador é obrigatório
                </small>
              </div>

              <div class="p-field" :class="$isMobile() ? 'p-col-12' : 'p-col-6'">
                <label for="email">E-mail</label>
                <InputText
                  id="email"
                  name="email"
                  v-validate="'required|email'"
                  :class="{ 'p-invalid': errors.has('email') }"
                  v-model="userForm.email"
                />
                <small v-if="errors.has('email')" class="p-error" role="alert">
                  E-mail válido é obrigatório
                </small>
              </div>

              <div class="p-field" :class="$isMobile() ? 'p-col-12' : 'p-col-6'">
                <label for="contact">Contacto</label>
                <InputText
                  id="contact"
                  name="contact"
                  v-model="userForm.contact"
                />
              </div>

              <div v-if="userForm.id == undefined" class="p-col-12">
                <div class="p-grid">
                  <div v-if="!userForm.passwordEmail"
                    class="p-field"
                    :class="$isMobile() ? 'p-col-12' : 'p-col-6'"
                  >
                    <label for="password">Palavra-passe</label>
                    <Password
                      v-model="userForm.password"
                      v-validate="'required|min:8'"
                      :feedback="false"
                      toggleMask
                      data-vv-name="password"
                    />
                    <small v-if="errors.has('password')" class="p-error">
                      Palavra-passe é obrigatória (mín. 8 caracteres)
                    </small>
                  </div>

                  <div v-if="!userForm.passwordEmail"
                    class="p-field"
                    :class="$isMobile() ? 'p-col-12' : 'p-col-6'"
                  >
                    <label for="confirmPassword">Confirmar Palavra-passe</label>
                    <Password
                      v-model="userForm.confirmPassword"
                      @input="validateConfirmPassword"
                      :feedback="false"
                      toggleMask
                    />
                    <small v-if="confirmPasswordError" class="p-error">
                      {{ confirmPasswordError }}
                    </small>
                  </div>

                  <div v-if="userForm.passwordEmail" class="p-col-12 p-mb-2">
                    <label for="confirmPassword">Palavra-passe</label>
                  </div>
                  <div class="field-checkbox p-mb-4" :class="$isMobile() ? 'p-col-12' : 'p-col-6'">
                    <Checkbox id="binary" v-model="userForm.passwordEmail" :binary="true" />
                    <label for="binary"> Definir Palavra-passe por e-mail</label>
                  </div>
                </div>
              </div>

              <div class="p-field p-col-12">
                <label for="clients">Clientes</label>
                <MultiSelect
                  id="clients"
                  v-model="userForm.clients"
                  :options="clientOptions"
                  optionLabel="name"
                  optionValue="id"
                  placeholder="Selecione clientes"
                  display="chip"
                  filter
                  filterPlaceholder="Pesquisar cliente"
                  name="clients"
                  v-validate="'required'"
                  :class="['ms-up', { 'p-invalid': errors.has('clients') }]"
                />
                <small v-if="errors.has('clients')" class="p-error" role="alert">
                  Tem de selecionar pelo menos 1
                </small>
              </div>


              <div class="p-field" :class="$isMobile() ? 'p-col-2' : 'p-col-1'">
                <label for="active">Activo</label>
              </div>
              <div class="p-field p-col-2">
                <InputSwitch id="active" v-model="userForm.active" />
              </div>

              <div class="p-field p-col-12">
                <label for="active">Autoriação para Criar Ordens de Serviço</label>
              </div>
              <div class="p-field p-col-2 -mt-10">
                <InputSwitch id="active" v-model="userForm.serviceOrderAuth" />
              </div>
            </div>
          </div>
        </div>

        <template #footer>
          <Button
            :label="'Guardar'"
            icon="pi pi-check"
            class="p-button-success"
            :disabled="dialogLoading"
            @click="saveUser"
          />
          <Button
            label="Cancelar"
            icon="pi pi-times"
            class="p-button-danger"
            :disabled="dialogLoading"
            @click="closeUserDialog"
          />
        </template>
      </Dialog>
    </div>
  </div>
</template>


<script>
import ExtranetUsersServices from "@/services/extranetUsers.service"
import { FilterMatchMode } from 'primevue/api'
import ClientService from "@/services/client.service";

export default {
  name: "ExtranetUsers",
  data () {
    return {
      loading: false,
      dialogLoading: false,
      columns: [
        { field: "name",    header: "Nome", clickable: true },
        { field: "email",   header: "Email" },
        { field: "contact",   header: "Contacto" },
        { field: "clientsString", header: "Clientes" },
        { field: "activeString",  header: "Activa" },
      ],
      filters: {
        name: { value: null, matchMode: FilterMatchMode.CONTAINS },
        email: { value: null, matchMode: FilterMatchMode.CONTAINS },
        contact: { value: null, matchMode: FilterMatchMode.CONTAINS },
        clientsString: { value: null, matchMode: FilterMatchMode.CONTAINS },
        activeString: { value: null, matchMode: FilterMatchMode.EQUALS },
      },
      extranetUsers: [],
      userDialog: false,
      userForm: {},
      clientOptions: []
    }
  },
  async created() {
    await this.loadUsers()
  },
  methods: {
    async loadUsers() {
      this.loading = true
      const users = await ExtranetUsersServices.getAllExtranetUsers()
      this.userClientsToString(users)
      this.extranetUsers = users
      this.loading = false
    },
    async createUser(userData) {
      this.dialogLoading = true
      userData.active = userData.active ? 1 : 0
      const response = await ExtranetUsersServices.create(userData)
      console.log(response)
      if (!response) {
        this.dialogLoading = false
        return this.$toast.add({
          severity: 'error',
          summary: 'ERROR',
          detail: 'Falha ao criar utilizador.',
          life: 3000,
        })
      }
      this.dialogLoading = false
      this.userDialog = false
      this.$toast.add({
        severity: 'success',
        summary: 'Utilizador criado com sucesso.',
        detail: "",
        life: 3000,
      })
      this.loadUsers()
    },
    async updateUser(userData) {
      this.dialogLoading = true
      userData.active = userData.active ? 1 : 0
      const response = await ExtranetUsersServices.update(userData.id, userData)
      console.log(response)
      if (!response) {
        this.dialogLoading = false
        return this.$toast.add({
          severity: 'error',
          summary: 'ERROR',
          detail: 'Falha ao atualizar utilizador.',
          life: 3000,
        })
      }
      this.dialogLoading = false
      this.userDialog = false
      this.$toast.add({
        severity: 'success',
        summary: 'Utilizador atualizado com sucesso.',
        detail: "",
        life: 3000,
      })
      this.loadUsers()
    },
    userClientsToString(users) {
      if (Array.isArray(users) && users.length > 0) {
        users.map(user => {
          user.activeString = user.active == 1 ? 'Sim' : 'Não'
          if (Array.isArray(user.clients) && user.clients.length > 0) {
            user.clientsString =  user.clients.map(c => typeof c === 'object' ? c.name : c)
            .filter(Boolean)
            .join(', ')
          } else {
            user.clientsString = ''
          }
        })
      }
    },
    openUserDialog(user = null) {
      if (user) {
        this.userForm = {
          id: user.id,
          name: user.name,
          username: user.username,
          email: user.email,
          contact: user.contact,
          clients: user.clients.map(clients => clients.id),
          active: user.active == 1,
          serviceOrderAuth: user.serviceOrderAuth == 1
        }
      } else {
        this.userForm = {
          id: null,
          name: "",
          username: "",
          email: "",
          password: Math.random().toString(36).slice(-8),
          passwordEmail: false,
          confirmPassword: "",
          contact: "",
          clients: [],
          active: true,
          serviceOrderAuth: true
        }
      }
      this.loadAllClients()
      this.userDialog = true;
    },
    closeUserDialog() {
      this.userDialog = false;
    },
    async loadAllClients() {
      const clients = await ClientService.getAll()
      this. clientOptions = clients
    },
    saveUser() {
      this.$validator.resume();
      this.$validator.validateAll().then((isValid) => {
        if (!isValid) return;

        this.$validator.pause();
        this.$validator.reset();

        const userData = JSON.parse(JSON.stringify(this.userForm))
        if (this.userForm.id == undefined) {
          this.createUser(userData)
        } else {
          this.updateUser(userData)
        }
      })
    },
    validateConfirmPassword() {
      if (!this.userForm.confirmPassword) {
        return "Confirmar palavra-passe é obrigatório";
      }
      if (this.userForm.confirmPassword !== this.userForm.password) {
        return "As palavras-passe não coincidem";
      }
      return null;
    }
  },
  computed: {
    confirmPasswordError() {
      return this.validateConfirmPassword();
    }
  }
}
</script>

<style>

.dialog-content-wrapper {
  position: relative;
}

.dialog-overlay {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
}

.dialog-spinner {
  font-size: 2.5rem;
  color: #ffffff;
}

.dialog-content-disabled {
  pointer-events: none;
  opacity: 0.5;
}

.ms-up .p-multiselect-panel {
  top: auto !important;
  bottom: 100%;
}

.ms-up .p-multiselect-items-wrapper {
  max-height: 20vh !important;
  overflow-y: auto;
}
</style>